import type { Viewport } from '@kanvas/board-core';
import { DURATION, easeInOutCubic, prefersReducedMotion } from '../render/motion';
import { useEditor } from './store';

/**
 * Animated camera moves.
 *
 * Zoom-to-fit and zoom-to-selection used to jump, which loses the one thing
 * that makes a large board navigable: knowing *where* you just went. Travelling
 * there over a few hundred milliseconds keeps that spatial link, so the user
 * does not have to re-orient after every jump.
 *
 * Zoom is interpolated geometrically rather than linearly. Zoom is a
 * multiplicative quantity — going 0.1 → 1.0 linearly spends most of the
 * animation at the far end and arrives with a lurch, while interpolating the
 * logarithm makes the change feel like a constant rate throughout.
 */

interface Tween {
  frame: number;
  start: number;
  duration: number;
  from: { x: number; y: number; zoom: number };
  to: { x: number; y: number; zoom: number };
  ease: (t: number) => number;
}

let tween: Tween | null = null;

export function cancelViewportAnimation(): void {
  if (!tween) return;
  cancelAnimationFrame(tween.frame);
  tween = null;
}

export function isViewportAnimating(): boolean {
  return tween !== null;
}

export interface CameraMoveOptions {
  duration?: number;
  /** Skip the animation and jump, e.g. while dragging the minimap. */
  immediate?: boolean;
  /** Defaults to ease-in-out; follow-mode tracking uses ease-out so it stays glued. */
  ease?: (t: number) => number;
}

export function animateViewportTo(
  target: Pick<Viewport, 'x' | 'y' | 'zoom'>,
  options: CameraMoveOptions = {},
): void {
  cancelViewportAnimation();

  const setViewport = useEditor.getState().setViewport;
  if (options.immediate || prefersReducedMotion()) {
    setViewport((vp) => ({ ...vp, ...target }));
    return;
  }

  const current = useEditor.getState().viewport;
  const from = { x: current.x, y: current.y, zoom: current.zoom };

  // Nothing worth animating: land it and skip the frame budget.
  const moved =
    Math.abs(from.x - target.x) > 0.5 ||
    Math.abs(from.y - target.y) > 0.5 ||
    Math.abs(from.zoom / target.zoom - 1) > 0.005;
  if (!moved) {
    setViewport((vp) => ({ ...vp, ...target }));
    return;
  }

  const duration = options.duration ?? DURATION.slow;
  const ease = options.ease ?? easeInOutCubic;
  const logFrom = Math.log(from.zoom);
  const logTo = Math.log(target.zoom);

  const step = (now: number) => {
    if (!tween) return;
    const t = Math.min(1, (now - tween.start) / tween.duration);
    const eased = tween.ease(t);

    setViewport((vp) => ({
      ...vp,
      x: from.x + (target.x - from.x) * eased,
      y: from.y + (target.y - from.y) * eased,
      zoom: Math.exp(logFrom + (logTo - logFrom) * eased),
    }));

    if (t >= 1) {
      tween = null;
      // Land exactly on the target: accumulated float error at the end of an
      // eased tween is visible as a half-pixel offset at high zoom.
      setViewport((vp) => ({ ...vp, ...target }));
      return;
    }
    tween.frame = requestAnimationFrame(step);
  };

  tween = {
    frame: requestAnimationFrame(step),
    start: performance.now(),
    duration,
    from,
    to: { ...target },
    ease,
  };
}
